"use client";

import * as React from "react";

import { KeyboardShortcutsDialog } from "../composites/keyboard-shortcuts-dialog";
import { isKeyboardShortcutTarget } from "./target";
import { KeyboardShortcutsContext, type ShortcutRegistration } from "./context";

export type KeyboardShortcutScope = "global" | "page" | "table";
export type KeyboardShortcutModifier = "mod" | "shift" | "alt";

export interface KeyboardShortcutStroke {
  key: string;
  modifiers?: KeyboardShortcutModifier[];
}

export interface KeyboardShortcutBinding {
  strokes: KeyboardShortcutStroke[];
}

export type KeyboardShortcutPolicy = boolean | ((event: KeyboardEvent) => boolean);

export interface KeyboardShortcutCommand {
  id: string;
  description: string;
  bindings: KeyboardShortcutBinding[];
  action: () => void;
  enabled?: boolean | (() => boolean);
  /** Hide commands whose capability is unavailable, independent of transient disabled state. */
  showInHelp?: boolean | (() => boolean);
  /** Allow this command to run when its key event originates in a control. */
  allowInInteractiveTarget?: KeyboardShortcutPolicy;
  /** Allow this command to run while a modal surface is open. */
  allowWhenModalOpen?: KeyboardShortcutPolicy;
  /** Opt in only for idempotent navigation such as holding j/k. */
  allowRepeat?: boolean;
}

const SEQUENCE_TIMEOUT_MS = 800;

export function shortcutKey(
  key: string,
  modifiers?: KeyboardShortcutModifier[],
): KeyboardShortcutBinding {
  return { strokes: [{ key, ...(modifiers ? { modifiers } : {}) }] };
}

export function shortcutSequence(...keys: string[]): KeyboardShortcutBinding {
  return { strokes: keys.map((key) => ({ key })) };
}

function commandEnabled(command: KeyboardShortcutCommand): boolean {
  return typeof command.enabled === "function" ? command.enabled() : command.enabled !== false;
}

function commandVisibleInHelp(command: KeyboardShortcutCommand): boolean {
  return typeof command.showInHelp === "function"
    ? command.showInHelp()
    : command.showInHelp !== false;
}

function policyAllows(policy: KeyboardShortcutPolicy | undefined, event: KeyboardEvent): boolean {
  return typeof policy === "function" ? policy(event) : policy === true;
}

function commandAllowedInTarget(
  command: KeyboardShortcutCommand,
  event: KeyboardEvent,
  interactiveTarget: boolean,
  modalOpen: boolean,
): boolean {
  return (
    (!interactiveTarget || policyAllows(command.allowInInteractiveTarget, event)) &&
    (!modalOpen || policyAllows(command.allowWhenModalOpen, event))
  );
}

function isMacPlatform(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
}

function displayStroke(stroke: KeyboardShortcutStroke): string[] {
  const modifiers = stroke.modifiers ?? [];
  const result: string[] = [];
  for (const modifier of modifiers) {
    if (modifier === "mod") result.push(isMacPlatform() ? "⌘" : "Ctrl");
    if (modifier === "shift") result.push(isMacPlatform() ? "⇧" : "Shift");
    if (modifier === "alt") result.push(isMacPlatform() ? "⌥" : "Alt");
  }
  result.push(stroke.key.length === 1 ? stroke.key.toUpperCase() : stroke.key);
  return result;
}

function displayBinding(binding: KeyboardShortcutBinding): string[] {
  return binding.strokes.flatMap((stroke) => displayStroke(stroke));
}

function strokeMatches(event: KeyboardEvent, stroke: KeyboardShortcutStroke): boolean {
  const modifiers = new Set(stroke.modifiers ?? []);
  const modMatches = modifiers.has("mod")
    ? event.metaKey || event.ctrlKey
    : !event.metaKey && !event.ctrlKey;
  return (
    event.key.toLowerCase() === stroke.key.toLowerCase() &&
    modMatches &&
    event.shiftKey === modifiers.has("shift") &&
    event.altKey === modifiers.has("alt")
  );
}

function elementIsVisible(element: HTMLElement | null): boolean {
  if (!element?.isConnected) return false;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (node.hidden || node.getAttribute("aria-hidden") === "true") return false;
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") return false;
  }
  return true;
}

function hasOpenModal(): boolean {
  // `data-non-modal` opts a layer out: a persistent drawer sits over the page
  // without trapping focus, and the page behind it must keep its shortcuts.
  return Boolean(
    document.querySelector(
      "[data-slot='dialog-content'][data-state='open']:not([data-non-modal]), [data-slot='alert-dialog-content'][data-state='open']:not([data-non-modal]), [role='dialog'][data-state='open']:not([data-non-modal]), [role='alertdialog'][data-state='open']:not([data-non-modal])",
    ),
  );
}

type PendingSequence = {
  candidates: Array<{
    command: KeyboardShortcutCommand;
    binding: KeyboardShortcutBinding;
    nextStroke: number;
  }>;
  timeout: ReturnType<typeof setTimeout>;
};

let warnedAboutMissingProvider = false;
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? React.useEffect : React.useLayoutEffect;

export function KeyboardShortcutsProvider({ children }: { children: React.ReactNode }) {
  const registrationsRef = React.useRef(new Map<symbol, ShortcutRegistration>());
  const claimedTableRef = React.useRef<symbol | null>(null);
  const pendingSequenceRef = React.useRef<PendingSequence | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const dialogOpenRef = React.useRef(dialogOpen);
  const [, setRegistryVersion] = React.useState(0);
  useIsomorphicLayoutEffect(() => {
    dialogOpenRef.current = dialogOpen;
  }, [dialogOpen]);

  const refresh = React.useCallback(() => {
    if (dialogOpenRef.current) setRegistryVersion((version) => version + 1);
  }, []);

  const register = React.useCallback(
    (registration: ShortcutRegistration) => {
      registrationsRef.current.set(registration.id, registration);
      refresh();
      return () => {
        if (registrationsRef.current.get(registration.id) !== registration) return;
        registrationsRef.current.delete(registration.id);
        if (claimedTableRef.current === registration.id) claimedTableRef.current = null;
        if (pendingSequenceRef.current) {
          clearTimeout(pendingSequenceRef.current.timeout);
          pendingSequenceRef.current = null;
        }
        refresh();
      };
    },
    [refresh],
  );

  const claimTable = React.useCallback((id: symbol) => {
    claimedTableRef.current = id;
  }, []);

  const activeRegistrations = React.useCallback((): ShortcutRegistration[] => {
    const registrations = [...registrationsRef.current.values()].filter((entry) => entry.active());
    const tables = registrations.filter(
      (entry) => entry.scope === "table" && elementIsVisible(entry.element?.() ?? null),
    );
    const claimed = tables.find((entry) => entry.id === claimedTableRef.current);
    const activeTable = claimed ?? (tables.length === 1 ? tables[0] : undefined);
    return [
      ...registrations.filter((entry) => entry.scope === "global"),
      ...registrations.filter((entry) => entry.scope === "page"),
      ...(activeTable ? [activeTable] : []),
    ];
  }, []);

  const activeCommands = React.useCallback(
    (dispatchOrder = false, forHelp = false): KeyboardShortcutCommand[] => {
      const registrations = activeRegistrations();
      if (dispatchOrder) registrations.reverse();
      return registrations
        .flatMap((entry) => entry.commands())
        .filter(forHelp ? commandVisibleInHelp : commandEnabled);
    },
    [activeRegistrations],
  );

  const clearPendingSequence = React.useCallback(() => {
    const pending = pendingSequenceRef.current;
    if (pending) clearTimeout(pending.timeout);
    pendingSequenceRef.current = null;
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const interactiveTarget = isKeyboardShortcutTarget(event.target);
      const modalOpen = hasOpenModal();
      if (event.defaultPrevented) return;

      if (
        !interactiveTarget &&
        !modalOpen &&
        event.key === "?" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        event.preventDefault();
        clearPendingSequence();
        setDialogOpen(true);
        return;
      }

      const pending = pendingSequenceRef.current;
      if (pending) {
        const matches = pending.candidates.filter(({ command, binding, nextStroke }) => {
          const stroke = binding.strokes[nextStroke];
          return stroke
            ? commandAllowedInTarget(command, event, interactiveTarget, modalOpen) &&
                strokeMatches(event, stroke)
            : false;
        });
        clearPendingSequence();
        const completed = matches.find(
          ({ binding, nextStroke }) => nextStroke === binding.strokes.length - 1,
        );
        if (completed) {
          if (!event.repeat || completed.command.allowRepeat) {
            event.preventDefault();
            completed.command.action();
          }
          return;
        }
        if (matches.length > 0) {
          const timeout = setTimeout(clearPendingSequence, SEQUENCE_TIMEOUT_MS);
          pendingSequenceRef.current = {
            candidates: matches.map((candidate) => ({
              ...candidate,
              nextStroke: candidate.nextStroke + 1,
            })),
            timeout,
          };
          event.preventDefault();
          return;
        }
      }

      const commands = activeCommands(true);
      const singleMatch = commands.find(
        (command) =>
          commandAllowedInTarget(command, event, interactiveTarget, modalOpen) &&
          command.bindings.some(
            (binding) => binding.strokes.length === 1 && strokeMatches(event, binding.strokes[0]!),
          ),
      );
      const sequenceCandidates = commands.flatMap((command) =>
        command.bindings
          .filter(
            (binding) =>
              commandAllowedInTarget(command, event, interactiveTarget, modalOpen) &&
              binding.strokes.length > 1 &&
              strokeMatches(event, binding.strokes[0]!),
          )
          .map((binding) => ({ command, binding, nextStroke: 1 })),
      );
      if (sequenceCandidates.length > 0 && !event.repeat) {
        event.preventDefault();
        const timeout = setTimeout(clearPendingSequence, SEQUENCE_TIMEOUT_MS);
        pendingSequenceRef.current = {
          candidates: sequenceCandidates,
          timeout,
        };
        return;
      }

      if (!singleMatch || (event.repeat && !singleMatch.allowRepeat)) return;
      event.preventDefault();
      singleMatch.action();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      clearPendingSequence();
    };
  }, [activeCommands, activeRegistrations, clearPendingSequence]);

  const value = React.useMemo(
    () => ({ register, claimTable, refresh }),
    [register, claimTable, refresh],
  );
  const helpCommands = dialogOpen ? activeCommands(false, true) : [];
  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
      <KeyboardShortcutsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        shortcuts={helpCommands
          .map((command) => ({
            keyGroups: command.bindings.map(displayBinding),
            description: command.description,
          }))
          .concat({ keyGroups: [["?"]], description: "Show keyboard shortcuts" })}
      />
    </KeyboardShortcutsContext.Provider>
  );
}

export function useKeyboardShortcutRegistration({
  scope,
  commands,
  active = true,
  elementRef,
}: {
  scope: KeyboardShortcutScope;
  commands: KeyboardShortcutCommand[];
  active?: boolean;
  elementRef?: React.RefObject<HTMLElement | null>;
}) {
  const registry = React.useContext(KeyboardShortcutsContext);
  const id = React.useRef(Symbol(`keyboard-shortcut-${scope}`));
  const commandsRef = React.useRef(commands);
  const activeRef = React.useRef(active);
  useIsomorphicLayoutEffect(() => {
    commandsRef.current = commands;
    activeRef.current = active;
    registry?.refresh();
  }, [active, commands, registry]);

  React.useEffect(() => {
    if (
      process.env.NODE_ENV !== "production" &&
      !registry &&
      activeRef.current &&
      commandsRef.current.length > 0 &&
      !warnedAboutMissingProvider
    ) {
      warnedAboutMissingProvider = true;
      console.warn(
        "Keyboard shortcuts are unavailable because KeyboardShortcutsProvider is missing. Wrap this surface in KeyboardShortcutsProvider to enable its commands.",
      );
    }
    if (!registry) return;
    return registry.register({
      id: id.current,
      scope,
      commands: () => commandsRef.current,
      active: () => activeRef.current,
      ...(elementRef ? { element: () => elementRef.current } : {}),
    });
  }, [registry, scope, elementRef]);

  return React.useCallback(() => registry?.claimTable(id.current), [registry]);
}
